import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Delete, KeyRound } from "lucide-react";

export function PinPad({ value, onChange, testId = "pin" }) {
  const press = (d) => value.length < 4 && onChange(value + d);
  const back = () => onChange(value.slice(0, -1));
  return (
    <div data-testid={`${testId}-pad`}>
      <div className="flex justify-center gap-4 mb-5" aria-label="PIN digits">
        {[0, 1, 2, 3].map((i) => <span key={i} data-testid={`${testId}-dot-${i}`} className={`w-6 h-6 rounded-full border-2 border-[#2E7D6B] ${value.length > i ? "bg-[#2E7D6B]" : "bg-white"}`} />)}
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button type="button" key={d} data-testid={`${testId}-key-${d}`} onClick={() => press(d)} className="min-h-[64px] text-3xl font-semibold rounded-2xl bg-white border-2 border-[#E2D9CE] hover:border-[#2E7D6B] active:bg-[#E9F3F0]">{d}</button>
        ))}
        <span />
        <button type="button" data-testid={`${testId}-key-0`} onClick={() => press("0")} className="min-h-[64px] text-3xl font-semibold rounded-2xl bg-white border-2 border-[#E2D9CE] hover:border-[#2E7D6B] active:bg-[#E9F3F0]">0</button>
        <button type="button" data-testid={`${testId}-key-back`} onClick={back} aria-label="Delete" className="min-h-[64px] rounded-2xl bg-white border-2 border-[#E2D9CE] hover:border-[#2E7D6B] flex items-center justify-center"><Delete className="w-7 h-7 text-slate-600" /></button>
      </div>
    </div>
  );
}

export default function PinLogin({ onSuccess }) {
  const [identifier, setIdentifier] = useState(() => localStorage.getItem("kindred_pin_identifier") || "");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const h = () => setIdentifier(localStorage.getItem("kindred_pin_identifier") || "");
    window.addEventListener("kindred-pin-prefill", h);
    return () => window.removeEventListener("kindred-pin-prefill", h);
  }, []);

  const submit = async () => {
    if (!identifier.trim() || pin.length !== 4) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/pin-login", { identifier: identifier.trim(), pin, remember: true });
      localStorage.setItem("kindred_pin_identifier", identifier.trim());
      onSuccess(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
      setPin("");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="pin-login" className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 space-y-5 shadow-sm">
      <div>
        <label className="block font-semibold text-slate-800 mb-2 text-lg">Your phone number or email</label>
        <input data-testid="pin-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="e.g. 98300 12345"
          className="w-full px-4 min-h-[56px] text-xl rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
      </div>
      <p className="text-center text-lg font-semibold text-slate-800">Enter your 4-digit PIN</p>
      <PinPad value={pin} onChange={setPin} />
      <button data-testid="pin-login-submit" onClick={submit} disabled={loading || pin.length !== 4 || !identifier.trim()}
        className="w-full bg-[#2E7D6B] hover:bg-[#256658] disabled:opacity-50 text-white font-semibold min-h-[60px] rounded-xl text-xl transition-colors flex items-center justify-center gap-2">
        <KeyRound className="w-6 h-6" /> {loading ? "Checking…" : "Sign in"}
      </button>
    </div>
  );
}
